import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { MODUL_NAME, rollenBezeichnung, type Ich, type Modul, type Umgebung } from '../../shared/rollen';
import { navigation, seitenleistenZusatz, type NavPunkt } from '../navigation';
import { gehe } from '../ort';
import { api } from '../api';
import { ZAEHLER_NEU } from '../daten';
import { Symbol, useEscape, useNavMarke, vars } from './Grundbausteine';

type Thema = 'light' | 'dark';

const abmelden = async () => { await api('/api/abmelden', { methode: 'POST' }); location.href = '/'; };

// Standard dunkel; die Wahl gilt je Person, sonst die zuletzt in diesem Browser gewählte (Leon 2026-10-28)
function themaLesen(id: number): Thema {
  try { return (localStorage.getItem(`nt-ansicht:${id}`) ?? localStorage.getItem('nt-ansicht')) === 'light' ? 'light' : 'dark'; } catch { return 'dark'; }
}

/* ---------- Kopfleiste ---------- */
function Kopfleiste({ ich, modul, sucheOeffnen, seiteOeffnen }: { ich: Ich; modul: Modul; sucheOeffnen: () => void; seiteOeffnen: () => void }) {
  const [thema, setThema] = useState<Thema>(() => themaLesen(ich.id));
  const [ichOffen, setIchOffen] = useState(false);
  useEffect(() => {
    document.documentElement.dataset.theme = thema;
    try { localStorage.setItem(`nt-ansicht:${ich.id}`, thema); localStorage.setItem('nt-ansicht', thema); } catch { /* ohne Speicher nur für diese Sitzung */ }
  }, [thema, ich.id]);
  const schliessen = useCallback(() => setIchOffen(false), []);
  useEscape(ichOffen, schliessen);
  const module = ich.module;
  return (
    <header className="topbar">
      <button className="btn btn-ghost icon-btn menu-btn" id="menuBtn" aria-label="Seitenleiste öffnen" onClick={seiteOeffnen}><Symbol id="menu" /></button>
      <div className="brand"><img className="brand-logo" src="/logo-zeichen.png" alt="" width={24} height={26} /><span className="brand-name">Neuratek</span></div>
      <nav className="seg modules" style={vars({ '--n': module.length, '--i': Math.max(0, module.indexOf(modul)) })} aria-label="Module" hidden={module.length < 2}>
        <span className="seg-thumb" aria-hidden="true" />
        {module.map(m => (
          <button key={m} type="button" aria-pressed={m === modul} data-mod={m} onClick={() => m !== modul && gehe(`/${m}`)}>
            <span className="long">{MODUL_NAME[m].lang}</span><span className="short">{MODUL_NAME[m].kurz}</span>
          </button>
        ))}
      </nav>
      <div className="topbar-end">
        <button className="btn search-btn" id="searchBtn" aria-label="Suchen" onClick={sucheOeffnen}><Symbol id="search" /><span>Firma, Liste, Seite …</span><kbd>Strg K</kbd></button>
        {modul === 'crm' && <Glocke />}
        <button className="btn btn-ghost icon-btn" id="themeBtn" aria-label={thema === 'dark' ? 'Helle Ansicht einschalten' : 'Dunkle Ansicht einschalten'} onClick={() => setThema(thema === 'dark' ? 'light' : 'dark')}>
          <Symbol id={thema === 'dark' ? 'sun' : 'moon'} />
        </button>
        <button className="avatar" title={ich.name} aria-label={`${ich.name}: Konto`} aria-expanded={ichOffen} onClick={() => setIchOffen(!ichOffen)}>{ich.kurz}</button>
      </div>
      <div className="pop ich" role="dialog" aria-label="Konto" hidden={!ichOffen}>
        <div><p style={{ fontWeight: 600 }}>{ich.name}</p><p className="meta">{ich.email}</p><p className="meta">{rollenBezeichnung(ich.rollen)}</p></div>
        <button className="btn btn-sm" style={{ alignSelf: 'flex-start' }} onClick={abmelden}>Abmelden</button>
      </div>
    </header>
  );
}

/* ---------- Glocke mit Meldungen (CRM, Prototyp renderBell) ---------- */
interface Meldung { id: number; text: string; ziel: string; wann: string; gelesen: boolean }
function Glocke() {
  const [offen, setOffen] = useState(false);
  const [liste, setListe] = useState<Meldung[]>([]);
  const laden = useCallback(() => { api<Meldung[]>('/api/crm/meldungen').then(setListe, () => undefined); }, []);
  useEffect(() => { laden(); addEventListener(ZAEHLER_NEU, laden); return () => removeEventListener(ZAEHLER_NEU, laden); }, [laden]);
  useEffect(() => {
    if (!offen) return;
    const zu = () => setOffen(false);
    document.addEventListener('click', zu);
    return () => document.removeEventListener('click', zu);
  }, [offen]);
  const schliessen = useCallback(() => setOffen(false), []);
  useEscape(offen, schliessen);
  const n = liste.filter(m => !m.gelesen).length;
  const gelesen = (id?: number) => api('/api/crm/aktion/meldung_gelesen', { methode: 'POST', daten: id ? { meldung: id } : {} }).then(laden, () => undefined);
  return (<>
    <button className="btn btn-ghost icon-btn bell" id="bellBtn" aria-label="Meldungen" onClick={e => { e.stopPropagation(); if (!offen) laden(); setOffen(!offen); }}><Symbol id="bell" /><span className="dotn" id="bellN" hidden={!n}>{n}</span></button>
    <div className="pop" id="bellPop" role="dialog" aria-label="Meldungen" hidden={!offen} onClick={e => e.stopPropagation()}>
      <div className="panel-head"><div><h2>Meldungen</h2><p className="meta">{n} neu</p></div><button className="btn btn-sm" data-allread onClick={() => gelesen()}>Alle gelesen</button></div>
      <ul className="note-list">{liste.map(m => <li key={m.id} className={m.gelesen ? 'read' : ''}><button data-note={m.id} onClick={() => { void gelesen(m.id); setOffen(false); gehe(`/crm/${m.ziel}`); }}><span className="nd" /><span><span>{m.text}</span><span className="meta">{m.wann}</span></span></button></li>)}</ul>
      {!liste.length && <p className="meta">Noch keine Meldungen.</p>}
    </div>
  </>);
}

/* ---------- Seitenleiste ---------- */
function Seitenleiste({ ich, modul, ansicht, punkte, umgebung, offen, schliessen, zaehler }: {
  ich: Ich; modul: Modul; ansicht: string; punkte: NavPunkt[]; umgebung: Umgebung; offen: boolean; schliessen: () => void; zaehler: Record<string, number>;
}) {
  const nav = useNavMarke(`${modul}/${ansicht}`);
  return (
    <>
      <aside className={offen ? 'sidebar open' : 'sidebar'} id="sidebar" aria-label={MODUL_NAME[modul].lang}>
        <div className="side-title"><h2>{MODUL_NAME[modul].lang}</h2><p className="meta">{seitenleistenZusatz(modul, ich)}</p></div>
        <nav className="nav" id="nav" ref={nav}>
          <span className="nav-ind" aria-hidden="true" />
          {punkte.flatMap(pkt => [
            ...(pkt.gruppe ? [<p key={`g-${pkt.id}`} className="nav-grp">{pkt.gruppe}</p>] : []),
            <button key={pkt.id} data-view={pkt.id} aria-current={pkt.id === ansicht ? 'page' : undefined} onClick={() => { gehe(`/${modul}/${pkt.id}`); schliessen(); }}>
              <Symbol id={pkt.symbol} />{pkt.titel}{zaehler[pkt.id] ? <span className="count" aria-label={`${zaehler[pkt.id]} offen`}>{zaehler[pkt.id]}</span> : null}
            </button>
          ])}
        </nav>
        {umgebung !== 'echt' && <div className="side-foot"><p className="meta">Testumgebung mit erfundenen Daten. Nichts hier geht an echte Firmen.</p></div>}
        <button className="btn btn-sm nur-handy" onClick={abmelden}>Abmelden ({ich.kurz})</button>
      </aside>
      <div className={offen ? 'side-scrim open' : 'side-scrim'} id="sideScrim" onClick={schliessen} />
    </>
  );
}

/* ---------- Suche (Strg K) ---------- */
export interface SuchEintrag { label: string; meta: string; ziel: string }

function Suche({ offen, schliessen, eintraege, fernSuche }: { offen: boolean; schliessen: () => void; eintraege: SuchEintrag[]; fernSuche: boolean }) {
  const [fern, setFern] = useState<SuchEintrag[]>([]);
  const [sichtbar, setSichtbar] = useState(false);
  const [an, setAn] = useState(false);
  const [q, setQ] = useState('');
  const [idx, setIdx] = useState(0);
  const feld = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (offen) {
      setQ(''); setIdx(0); setSichtbar(true);
      requestAnimationFrame(() => requestAnimationFrame(() => setAn(true)));
      setTimeout(() => feld.current?.focus(), 0);
      return;
    }
    setAn(false);
    const t = setTimeout(() => setSichtbar(false), 130);
    return () => clearTimeout(t);
  }, [offen]);
  // Listen und Firmen sucht der Server (Akquise)
  useEffect(() => {
    const w = q.trim();
    if (!fernSuche || w.length < 2) { setFern([]); return; }
    const t = setTimeout(() => {
      api<{ listen: { id: number; name: string; land: string; kat: string }[]; firmen: { fid: string; name: string; ort: string | null; liste: number | null }[] }>(`/api/akquise/suche?q=${encodeURIComponent(w)}`).then(r => setFern([
        ...r.listen.map(l => ({ label: l.name, meta: `${l.land} › ${l.kat}`, ziel: `/akquise/listen/${l.id}` })),
        ...r.firmen.map(f => ({ label: f.name, meta: `${f.fid}${f.ort ? ` · ${f.ort}` : ''}`, ziel: `/akquise/listen/${f.liste ?? ''}?firma=${f.fid}` }))
      ]), () => setFern([]));
    }, 150);
    return () => clearTimeout(t);
  }, [q, fernSuche]);
  const treffer = [...eintraege.filter(e => (e.label + ' ' + e.meta).toLowerCase().includes(q.trim().toLowerCase())), ...fern];
  const gewaehlt = Math.min(idx, Math.max(treffer.length - 1, 0));
  const ausfuehren = (i: number) => { const e = treffer[i]; if (!e) return; schliessen(); gehe(e.ziel); };
  return (
    <div className={an ? 'palette-wrap open' : 'palette-wrap'} id="palette" hidden={!sichtbar}>
      <div className="scrim open" id="palScrim" onClick={schliessen} />
      <div className="palette" role="dialog" aria-modal="true" aria-label="Suchen">
        <label className="field"><Symbol id="search" />
          <input ref={feld} className="input" id="palQ" type="text" placeholder="Seite, Liste oder Firma suchen" aria-label="Suchen" autoComplete="off" value={q}
            onChange={e => { setQ(e.target.value); setIdx(0); }}
            onKeyDown={e => {
              if (e.key === 'ArrowDown') { e.preventDefault(); setIdx(Math.min(gewaehlt + 1, treffer.length - 1)); }
              if (e.key === 'ArrowUp') { e.preventDefault(); setIdx(Math.max(gewaehlt - 1, 0)); }
              if (e.key === 'Enter') { e.preventDefault(); ausfuehren(gewaehlt); }
            }} />
        </label>
        <ul id="palList" role="listbox">
          {treffer.length
            ? treffer.map((e, i) => <li key={e.ziel + e.label}><button role="option" className={i === gewaehlt ? 'active' : ''} aria-selected={i === gewaehlt} onClick={() => ausfuehren(i)}>{e.label}<span className="meta">{e.meta}</span></button></li>)
            : <li className="none">Nichts gefunden zu „{q}“.</li>}
        </ul>
      </div>
    </div>
  );
}

/* ---------- Rahmen: Kopfleiste, Seitenleiste, Arbeitsfläche ---------- */
export function Rahmen({ ich, umgebung, modul, ansicht, zusatzSuche = [], zaehler = {}, children }: {
  ich: Ich; umgebung: Umgebung; modul: Modul; ansicht: string; zusatzSuche?: SuchEintrag[]; zaehler?: Record<string, number>; children: ReactNode;
}) {
  const [seiteOffen, setSeiteOffen] = useState(false);
  const [sucheOffen, setSucheOffen] = useState(false);
  const punkte = navigation(modul, ich);
  const eintraege = useMemo<SuchEintrag[]>(() => [
    ...ich.module.flatMap(m => navigation(m, ich).map(pkt => ({ label: pkt.titel, meta: ich.module.length > 1 ? `Seite · ${MODUL_NAME[m].lang}` : 'Seite', ziel: `/${m}/${pkt.id}` }))),
    ...zusatzSuche
  ], [ich, zusatzSuche]);

  useEffect(() => {
    const taste = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setSucheOffen(o => !o); }
      if (e.key === 'Escape') { setSucheOffen(false); setSeiteOffen(false); }
    };
    document.addEventListener('keydown', taste);
    return () => document.removeEventListener('keydown', taste);
  }, []);

  return (
    <>
      <Kopfleiste ich={ich} modul={modul} sucheOeffnen={() => setSucheOffen(true)} seiteOeffnen={() => setSeiteOffen(true)} />
      <div className="shell" inert={sucheOffen || undefined}>
        <Seitenleiste ich={ich} modul={modul} ansicht={ansicht} punkte={punkte} umgebung={umgebung} offen={seiteOffen} schliessen={() => setSeiteOffen(false)} zaehler={zaehler} />
        <main id="main">{children}</main>
      </div>
      <Suche offen={sucheOffen} schliessen={() => setSucheOffen(false)} eintraege={eintraege} fernSuche={ich.module.includes("akquise")} />
    </>
  );
}
